<template>
  <md-card class="md-card-chart">
    <md-card-header>
      <div v-if="ready" class="graph-wrapper md-card-header-blue">
        <div ref="chart" class="graph-chart"></div>
      </div>
    </md-card-header>
    <md-card-content style="display: flex;">
      <div v-if="chartInsideContent" :id="chartId" class="ct-chart"></div>
      <div class="md-card-action-buttons text-center" v-if="headerAnimation === 'true'">
        <md-button class="md-danger md-simple fix-broken-card" @click="fixHeader" v-if="headerDown">
          <slot name="fixed-button"></slot> Fix Header!
        </md-button>
        <slot name="first-button"></slot>
        <slot name="second-button"></slot>
        <slot name="third-button"></slot>
      </div>
      <slot name="content"><svg-icon type="mdi" :path="icons.trendingUp"></svg-icon> Evolución de renta esperada</slot>
    </md-card-content>
  </md-card>
</template>

<script>
import SvgIcon from '@jamescoyle/vue-icon';
import { mdiTrendingUp } from '@mdi/js';

export default {
  name: 'graph-card',
  props: ['data', 'headers'],
  data() {
    return {
      ready: false,
      chart: null,
      echarts: null,
      icons: {
        trendingUp: mdiTrendingUp
      }
    }
  },
  components: {
    SvgIcon
  },
  watch: {
    data: {
      handler() {
        this.renderChart()
      },
      deep: true
    },
    headers() {
      this.renderChart()
    }
  },
  mounted() {
    this.ready = true
    this.$nextTick(() => {
      this.renderChart()
      window.addEventListener('resize', this.onResize)
    })
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
    if (this.chart) {
      this.chart.dispose()
      this.chart = null
    }
  },
  methods: {
    formatHeader(index) {
      if (!this.headers || !this.headers[index]) return ''
      const partial = String(this.headers[index]).replace(/_/g, ' ')
      return partial.charAt(0).toUpperCase() + partial.slice(1)
    },
    getData(key) {
      const v = this.data && key != null ? this.data[key] : null

      if (v === null || v === undefined) return null

      const value = String(v).trim()
      if (value === '' || value === '-') return null

      const n = Number(value)
      return Number.isFinite(n) ? n : null
    },
    async ensureEcharts() {
      if (this.echarts) return this.echarts
      const mod = await import('echarts')
      this.echarts = mod.default || mod
      return this.echarts
    },
    buildOption() {
      // X labels (as in screenshot)
      const labels = [
        this.formatHeader(12),
        this.formatHeader(13),
        this.formatHeader(14),
        this.formatHeader(15)
      ].map((v) => (v ? v : ''))

      // Series values
      const values = [
        this.getData(this.headers?.[12]),
        this.getData(this.headers?.[13]),
        this.getData(this.headers?.[14]),
        this.getData(this.headers?.[15])
      ]

      // Y bounds
      const low = this.getData(this.headers?.[11])
      const high = this.getData(this.headers?.[10])

      const validValues = values.filter((value) => Number.isFinite(value))

      const yMin = Number.isFinite(low)
        ? low
        : (validValues.length ? Math.min(...validValues) : null)

      const yMax = Number.isFinite(high)
        ? high
        : (validValues.length ? Math.max(...validValues) : null)

      return {
        backgroundColor: 'transparent',
        grid: { top: 10, right: 1, bottom: 18, left: 40, containLabel: false },
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'line' },
          backgroundColor: 'rgba(0,0,0,0.55)',
          borderWidth: 0,
          textStyle: { color: '#fff', fontWeight: 600 },
          formatter: (params) => {
            const formatNumber = (value) => {
              if (value === null || value === undefined || value === '') return ''
              const num = Number(value)
              if (Number.isNaN(num)) return value

              const isInt = Number.isInteger(num)
              return new Intl.NumberFormat('es-AR', {
                minimumFractionDigits: isInt ? 0 : 1,
                maximumFractionDigits: isInt ? 0 : 1
              }).format(num)
            }

            const p = Array.isArray(params) ? params[0] : params
            const name = p.axisValueLabel || p.name
            const val = p.data

            if (val === null || val === undefined || val === '') {
              return `${name}: -`
            }

            const formatted = formatNumber(val)
            return formatted === '' ? `${name}: -` : `${name}: ${formatted}%`
          }
        },
        xAxis: {
          type: 'category',
          boundaryGap: true,
          data: labels,
          axisTick: { show: true, lineStyle: { color: 'rgba(255,255,255,0.28)' } },
          axisLine: { show: true, lineStyle: { color: 'rgba(255,255,255,0.28)' } },
          axisLabel: {
            color: 'rgba(255,255,255,0.9)',
            fontSize: 12,
            fontWeight: 700,
            margin: 5
          },
          splitLine: {
            show: true,
            lineStyle: {
              type: 'dashed',
              color: 'rgba(255,255,255,0.28)'
            }
          }
        },
        yAxis: {
          type: 'value',
          min: yMin,
          max: yMax,
          splitNumber: 2,
          minInterval: 5,
          axisLine: { show: true, lineStyle: { color: 'rgba(255,255,255,0.28)' } },
          axisTick: { show: true, lineStyle: { color: 'rgba(255,255,255,0.28)' } },
          axisLabel: {
            color: 'rgba(255,255,255,0.9)',
            fontSize: 12,
            fontWeight: 700,
            formatter: (v) => {
              const num = Number(v)
              if (Number.isNaN(num)) return `${v}%`

              const isInt = Number.isInteger(num)
              return `${new Intl.NumberFormat('es-AR', {
                minimumFractionDigits: isInt ? 0 : 1,
                maximumFractionDigits: isInt ? 0 : 1
              }).format(num)}%`
            },
            margin: 5
          },
          splitLine: {
            show: true,
            lineStyle: {
              type: 'dashed',
              color: 'rgba(255,255,255,0.28)'
            }
          }
        },
        series: [
          {
            name: 'Evolución',
            type: 'line',
            data: values,
            smooth: false,
            symbol: 'circle',
            symbolSize: 6,
            showSymbol: true,
            connectNulls: false,
            lineStyle: { width: 4, color: 'rgba(255,255,255,0.92)' },
            itemStyle: {
              color: 'rgba(255,255,255,0.92)',
              borderColor: 'rgba(255,255,255,0.92)',
              borderWidth: 2
            },
            // Show values on the points (optional; keep it subtle)
            label: {
              show: true,
              position: 'top',
              color: 'rgba(255,255,255,0.92)',
              fontWeight: 700,
              fontSize: 12,
              formatter: (p) => {
                const num = Number(p.value)
                if (Number.isNaN(num)) return `${p.value}%`

                const isInt = Number.isInteger(num)
                return `${new Intl.NumberFormat('es-AR', {
                  minimumFractionDigits: isInt ? 0 : 1,
                  maximumFractionDigits: isInt ? 0 : 1
                }).format(num)}%`
              }
            }
          }
        ]
      }
    },
    async renderChart() {
      if (!this.ready || !this.$refs.chart) return

      try {
        const echarts = await this.ensureEcharts()
        if (!this.chart) {
          this.chart = echarts.init(this.$refs.chart)
        }
        this.chart.setOption(this.buildOption(), true)
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('ECharts load/render failed:', e)
      }
    },
    onResize() {
      if (this.chart) this.chart.resize()
    }
  }
}
</script>

<style scoped>
.graph-wrapper {
  border-radius: 10px;
  box-shadow: 0 18px 30px rgba(0, 0, 0, 0.18);
  padding: 14px 16px 10px;
  margin-top: -20px;
}

.graph-chart {
  width: 100%;
  height: 85px;
}

@media only screen and (max-width: 1185px) {
    .md-card-content {
        font-size: 12px;
    }
}
</style>
